import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DatabaseSelect,
  DbColumnMappingRow,
  parseDbLookupValue,
  serializeDbLookupValue,
  useDatabaseList,
  useVariableList,
  type DbLookupValue,
} from './DbLookupEditor';

// Schedule Lookup editor (ModuleParameter.typeId === 21).
//
// Shares the legacy persistence shape with the Data Lookup editor (see
// DbLookupEditor.tsx) but with two semantic differences:
//
//  - Columns 1 (Date) and 2 (Time) are implicit. The engine matches the
//    current call's local date+time against them; the user never picks a
//    search value here. We surface those two columns with a "Date" / "Time"
//    tag so the user still sees the fixture, but they are read-only.
//
//  - Variable mapping starts at column 3. Stored JSON only contains
//    `col_3..col_N` (no searchColumn / searchValue), matching the legacy
//    ParameterEditorDbLookupSchedule output.

interface Props {
  value: string;
  readOnly: boolean;
  applicationId: number;
  commit: (next: string) => void;
}

export function DbLookupScheduleEditor({ value, readOnly, applicationId, commit }: Props) {
  const parsed = useMemo(() => parseDbLookupValue(value), [value]);
  const [draft, setDraft] = useState<DbLookupValue>(parsed);
  useEffect(() => setDraft(parsed), [parsed]);

  const { items: databases, loading: dbLoading, error: dbError } = useDatabaseList(applicationId);
  const { items: variables, loading: varLoading } = useVariableList(applicationId);

  const dbColumns = useMemo(() => {
    if (!draft.simpleDbId) return [] as Array<{ index: number; name: string }>;
    const db = databases.find((d) => Number(d.id) === draft.simpleDbId);
    if (!db?.columns) return [];
    const out: Array<{ index: number; name: string }> = [];
    for (let i = 0; i < db.columns.length; i++) {
      const c = db.columns[i];
      if (!c?.name) break;
      out.push({ index: i + 1, name: c.name });
    }
    return out;
  }, [databases, draft.simpleDbId]);

  // Strip any col_1 / col_2 entries when serialising — those would confuse
  // the engine since they're the implicit date/time columns. Also pass
  // includeSearch=false because the schedule lookup never stores
  // searchColumn / searchValue.
  const persist = useCallback(
    (next: DbLookupValue) => {
      const sanitisedCols: Record<number, string> = {};
      for (const [k, v] of Object.entries(next.columnVariables)) {
        const idx = Number(k);
        if (idx >= 3 && v) sanitisedCols[idx] = v;
      }
      const sanitised: DbLookupValue = {
        ...next,
        columnVariables: sanitisedCols,
        searchColumn: null,
        searchValue: '',
      };
      const serialized = serializeDbLookupValue(sanitised, { includeSearch: false });
      if (serialized !== value) commit(serialized);
    },
    [commit, value],
  );

  const setDatabase = (id: number | null, name: string | null) => {
    const next: DbLookupValue = {
      simpleDbId: id,
      dbName: name,
      searchColumn: null,
      searchValue: '',
      columnVariables: {},
    };
    setDraft(next);
    persist(next);
  };

  const setColumnVariable = (colIndex: number, varId: string) => {
    const cols = { ...draft.columnVariables };
    if (varId) cols[colIndex] = varId;
    else delete cols[colIndex];
    const next = { ...draft, columnVariables: cols };
    setDraft(next);
    persist(next);
  };

  if (dbError) {
    return <div className="fb-error-inline">Failed to load databases: {dbError}</div>;
  }

  return (
    <div className="fb-db-editor">
      <div className="fb-db-row">
        <span className="fb-db-row-lbl">Database</span>
        <DatabaseSelect
          value={draft.simpleDbId}
          databases={databases}
          loading={dbLoading}
          readOnly={readOnly}
          onChange={setDatabase}
        />
      </div>

      {draft.simpleDbId != null && dbColumns.length > 0 && (
        <>
          <div className="fb-subsection">Schedule columns</div>
          {dbColumns.map((c) => {
            // Columns 1 and 2 are implicit Date / Time — surfaced read-only
            // so the user understands the fixture, but not mappable.
            const fixedLabel = c.index === 1 ? 'Date (implicit)' : c.index === 2 ? 'Time (implicit)' : undefined;
            return (
              <DbColumnMappingRow
                key={c.index}
                index={c.index}
                columnName={c.name}
                value={draft.columnVariables[c.index] ?? ''}
                fixedLabel={fixedLabel}
                variables={variables}
                variablesLoading={varLoading}
                readOnly={readOnly}
                onChange={(v) => setColumnVariable(c.index, v)}
                applicationId={applicationId}
              />
            );
          })}
        </>
      )}

      {draft.simpleDbId != null && dbColumns.length === 0 && !dbLoading && (
        <div className="fb-muted">No columns defined for this database.</div>
      )}
    </div>
  );
}
